import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { ChevronDown } from '@saleshandy/icons';

import { NavItem as NavItemType, SubNavItem as SubNavItemType } from '../types';

import NavItemElement from './nav-element';
import NavBadges from './nav-badges';
import SubNavItem from './sub-nav-item';

import { getNavItemIcon } from '../utils/get-nav-item-icon';
import { getNavItemDemoInfo } from '../utils/get-nav-item-demo-info';
import { accessibleOnClick } from '../../../../../shared/utils/accessible-on-click';

interface NavItemProps {
  itemKey: string;
  route: string;
  text: string;
  subNavItems: SubNavItemType[];
  count?: number;
  hasVisitedAgencyPortal: boolean;
  isExpanded: boolean;
  showInfoOnDemo?: boolean;
  shouldShowSidebarInfo?: boolean;
  handleShouldShowSidebarInfo?: VoidFunction;
  currentNavItem?: NavItemType;
  preservedRouteForCRM?: string | null;
}

const NavItem: React.FC<NavItemProps> = ({
  itemKey,
  route,
  text,
  subNavItems,
  hasVisitedAgencyPortal,
  isExpanded,
  count = 0,
  showInfoOnDemo,
  shouldShowSidebarInfo = false,
  handleShouldShowSidebarInfo = () => null,
  preservedRouteForCRM = null,
}) => {
  const history = useHistory();

  const isSubNavMenuSelected = useMemo(
    () =>
      subNavItems?.some(
        (subItem) =>
          subItem.route !== '' &&
          history?.location?.pathname?.startsWith(subItem.route),
      ),
    [subNavItems, history.location.pathname],
  );

  const subNavRef = useRef<HTMLDivElement>(null);
  const [subNavHeight, setSubNavHeight] = useState(0);
  const [isSubNavOpen, setIsSubNavOpen] = useState(false);

  const handleSubNavMenuOpen = (v: boolean) => {
    if (!isSubNavMenuSelected) {
      setIsSubNavOpen(v);
    }
  };

  const navItemRoute = useMemo(() => {
    // ** preserving the route for prospects to /crm/list or /crm/kanban view for session
    if (itemKey === 'prospects') {
      return preservedRouteForCRM || '/crm/list';
    }
    return route;
  }, [itemKey, route, preservedRouteForCRM]);

  useEffect(() => {
    if (isSubNavOpen && subNavRef.current) {
      setSubNavHeight(subNavRef.current.scrollHeight);
    } else {
      setSubNavHeight(0);
    }
  }, [isSubNavOpen]);

  useEffect(() => {
    if (isExpanded) {
      setIsSubNavOpen(isSubNavMenuSelected);
    } else {
      setIsSubNavOpen(false);
    }
  }, [isExpanded, isSubNavMenuSelected]);

  const navItemClassNames = classNames([
    'app-sidebar__nav-item',
    {
      active: route !== '' && history?.location?.pathname?.startsWith(route),
      'sub-nav-item-active':
        !isExpanded &&
        subNavItems?.some(
          (subItem) =>
            subItem.route !== '' &&
            history?.location?.pathname?.startsWith(subItem.route),
        ),
    },
  ]);

  return (
    <div
      className={classNames([
        'app-sidebar__nav-item-container',
        {
          active:
            route !== '' && history?.location?.pathname?.startsWith(route),
          isDemoAccount: shouldShowSidebarInfo && showInfoOnDemo,
          isSubNavOpen,
        },
      ])}
      {...(subNavItems?.length > 0
        ? {
            onMouseEnter: () => handleSubNavMenuOpen(true),
            onMouseLeave: () => handleSubNavMenuOpen(false),
          }
        : {})}
    >
      <NavItemElement itemRoute={navItemRoute} className={navItemClassNames}>
        <div className="nav-item-left">
          <div className="nav-item-icon">
            {getNavItemIcon(itemKey)}
            <NavBadges
              type="collapsed"
              itemKey={itemKey}
              shouldShowSidebarInfo={shouldShowSidebarInfo}
              showInfoOnDemo={showInfoOnDemo}
              count={count}
              hasVisitedAgencyPortal={hasVisitedAgencyPortal}
            />
          </div>
          <span className="nav-item-text">{text}</span>
        </div>

        <div className="nav-item-right">
          <NavBadges
            type="expanded"
            itemKey={itemKey}
            shouldShowSidebarInfo={shouldShowSidebarInfo}
            showInfoOnDemo={showInfoOnDemo}
            count={count}
            hasVisitedAgencyPortal={hasVisitedAgencyPortal}
          />
        </div>
      </NavItemElement>

      {subNavItems?.length > 0 && (
        <div
          className="app-sidebar__nav-item-chevron"
          {...accessibleOnClick(() => {
            handleSubNavMenuOpen(!isSubNavOpen);
          })}
        >
          <ChevronDown />
        </div>
      )}

      {subNavItems?.length > 0 && (
        <div
          className="app-sidebar__sub-nav-items"
          style={{
            height: subNavHeight,
            transition: 'height 0.3s ease-in-out',
          }}
        >
          <div ref={subNavRef} className="sub-nav-items-wrapper">
            <div className="sub-nav-items-divider" />

            <div className="sub-nav-items-container">
              {subNavItems.map((item, i) => (
                <SubNavItem
                  item={item}
                  index={i}
                  isSubNavOpen={isSubNavOpen}
                  isExpanded={isExpanded}
                  shouldShowSidebarInfo={shouldShowSidebarInfo}
                  showInfoOnDemo={showInfoOnDemo}
                  count={count}
                  hasVisitedAgencyPortal={hasVisitedAgencyPortal}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {shouldShowSidebarInfo &&
        showInfoOnDemo &&
        getNavItemDemoInfo(itemKey, handleShouldShowSidebarInfo)}
    </div>
  );
};

export default NavItem;
